import { baseApi } from "@/shared/api";
import type {
  CreativeReport,
  CreativeReportCreatePayload,
  CreativeReportListItem,
  MonitoringPayload,
  ReportLanguage,
  ReportPayload,
  ReportPhotoPoolItem,
} from "../types";

interface GenerateResponse {
  url: string;
  report: CreativeReport;
}

interface PdfLinkResponse {
  url: string;
  pdf_lang: ReportLanguage;
  generated_at: string | null;
  is_pdf_stale: boolean;
}

interface PhotoPoolResponse {
  items: ReportPhotoPoolItem[];
}

interface MonitoringArg {
  projectId?: number | null;
  startDate?: string;
  endDate?: string;
}

function monitoringQueryString({ projectId, startDate, endDate }: MonitoringArg): string {
  const params = new URLSearchParams();
  if (typeof projectId === "number") params.set("project_id", String(projectId));
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  const query = params.toString();
  return query ? `?${query}` : "";
}

interface AttachmentUploadResponse {
  key: string;
  url: string;
  name: string;
}

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCreativeReports: builder.query<CreativeReportListItem[], number | void>({
      query: (projectId) => ({
        url: `/api/projects/reports/${typeof projectId === "number" ? `?project_id=${projectId}` : ""}`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "ProjectReports", id: "LIST" }],
    }),
    getCreativeReport: builder.query<CreativeReport, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "ProjectReports", id }],
    }),
    getReportData: builder.query<ReportPayload, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/data/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "ProjectReports", id: `DATA-${id}` }],
    }),
    createCreativeReport: builder.mutation<CreativeReport, CreativeReportCreatePayload>({
      query: (body) => ({
        url: "/api/projects/reports/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "ProjectReports", id: "LIST" }],
    }),
    updateCreativeReport: builder.mutation<
      CreativeReport,
      { id: number; body: Partial<CreativeReport> }
    >({
      query: ({ id, body }) => ({
        url: `/api/projects/reports/${id}/`,
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ProjectReports", id },
        { type: "ProjectReports", id: `DATA-${id}` },
        { type: "ProjectReports", id: "LIST" },
      ],
    }),
    deleteCreativeReport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "ProjectReports", id: "LIST" }],
    }),
    generateCreativeReport: builder.mutation<GenerateResponse, { id: number; lang?: ReportLanguage }>({
      query: ({ id, lang }) => ({
        url: `/api/projects/reports/${id}/generate/`,
        method: "POST",
        body: lang ? { lang } : undefined,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ProjectReports", id },
        { type: "ProjectReports", id: "LIST" },
      ],
    }),
    // Ссылка на уже сформированный PDF без перегенерации — единственный способ
    // скачать документ отправленного донору (замороженного) отчета.
    getReportPdfLink: builder.query<PdfLinkResponse, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/pdf/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
    submitCreativeReport: builder.mutation<CreativeReport, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/submit/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ProjectReports", id },
        { type: "ProjectReports", id: `DATA-${id}` },
        { type: "ProjectReports", id: "LIST" },
      ],
    }),
    reopenCreativeReport: builder.mutation<CreativeReport, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/reopen/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ProjectReports", id },
        { type: "ProjectReports", id: `DATA-${id}` },
        { type: "ProjectReports", id: "LIST" },
      ],
    }),
    getProjectMonitoring: builder.query<MonitoringPayload, MonitoringArg>({
      query: (arg) => ({
        url: `/api/projects/monitoring/${monitoringQueryString(arg)}`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
    getReportPhotoPool: builder.query<PhotoPoolResponse, number>({
      query: (id) => ({
        url: `/api/projects/reports/${id}/photo-pool/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "ProjectReports", id: `PHOTOS-${id}` }],
    }),
    uploadReportPhotos: builder.mutation<PhotoPoolResponse, { id: number; files: File[] }>({
      query: ({ id, files }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        return {
          url: `/api/projects/reports/${id}/photos/`,
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: "ProjectReports", id: `PHOTOS-${id}` }],
    }),
    deleteReportPhoto: builder.mutation<CreativeReport, { id: number; key: string }>({
      query: ({ id, key }) => ({
        url: `/api/projects/reports/${id}/photos/delete/?key=${encodeURIComponent(key)}`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ProjectReports", id },
        { type: "ProjectReports", id: `DATA-${id}` },
        { type: "ProjectReports", id: `PHOTOS-${id}` },
      ],
    }),
    uploadReportPhotoStaging: builder.mutation<PhotoPoolResponse, { projectId: number; files: File[] }>({
      query: ({ projectId, files }) => {
        const formData = new FormData();
        formData.append("project_id", String(projectId));
        files.forEach((file) => formData.append("files", file));
        return {
          url: "/api/projects/reports/photo-staging/",
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
    }),
    deleteStagedReportPhoto: builder.mutation<void, { projectId: number; key: string }>({
      query: ({ projectId, key }) => ({
        url: `/api/projects/reports/photo-staging/delete/?project_id=${projectId}&key=${encodeURIComponent(key)}`,
        method: "DELETE",
        credentials: "include" as const,
      }),
    }),
    uploadReportAttachment: builder.mutation<
      AttachmentUploadResponse,
      { id: number; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/api/projects/reports/${id}/attachments/`,
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
    }),
    uploadReportAttachmentStaging: builder.mutation<
      AttachmentUploadResponse,
      { projectId: number; file: File }
    >({
      query: ({ projectId, file }) => {
        const formData = new FormData();
        formData.append("project_id", String(projectId));
        formData.append("file", file);
        return {
          url: "/api/projects/reports/attachment-staging/",
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
    }),
    deleteStagedReportAttachment: builder.mutation<void, { projectId: number; key: string }>({
      query: ({ projectId, key }) => ({
        url: `/api/projects/reports/attachment-staging/delete/?project_id=${projectId}&key=${encodeURIComponent(key)}`,
        method: "DELETE",
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useUploadReportAttachmentMutation,
  useUploadReportAttachmentStagingMutation,
  useDeleteStagedReportAttachmentMutation,
  useGetCreativeReportsQuery,
  useGetCreativeReportQuery,
  useGetReportDataQuery,
  useCreateCreativeReportMutation,
  useUpdateCreativeReportMutation,
  useDeleteCreativeReportMutation,
  useGenerateCreativeReportMutation,
  useLazyGetReportPdfLinkQuery,
  useSubmitCreativeReportMutation,
  useReopenCreativeReportMutation,
  useGetProjectMonitoringQuery,
  useGetReportPhotoPoolQuery,
  useUploadReportPhotosMutation,
  useDeleteReportPhotoMutation,
  useUploadReportPhotoStagingMutation,
  useDeleteStagedReportPhotoMutation,
} = reportsApi;
